import { motion } from 'framer-motion';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';

import Button from '../../shared/buttonComponents/Button';
import SelectBox from '../../shared/select-boxes/SelectBox';
import { useWizard } from '../../../contexts/WizardContext';
import { useMediaQuery } from '../../../contexts/MediaQueryContext';
import OutsideClickHandler from 'react-outside-click-handler';

interface RegionsListItem {
  name: string;
  icon: string;
  gallery: any;
  selected: boolean;
}

interface AddSolutionsPopupInterface {
  continent?: string;
  countriesList?: RegionsListItem[];
  onClose: () => void;
  onSave?: (selected: any) => void;
}


const AddSolutionsPopup: FC<AddSolutionsPopupInterface> = ({ onClose, onSave }): JSX.Element => {
  const {
    data,
    updateStepData,
    staticData
  } = useWizard();

  const { onMobile } = useMediaQuery();

  const regionsList = useMemo((): RegionsListItem[] => {
    return staticData.solutions.map((solution) => ({
      name: solution.name,
      gallery: solution.gallery,
      icon: `regions/${solution.name.toLocaleLowerCase().replace(/\\s/g, '-')}`,
      selected: false
    }));
  }, [staticData.solutions]);

  const [regions, setRegions] = useState<RegionsListItem[]>(regionsList);

  useEffect(() => {
    if (!data.solutions) {
      return;
    }

    setRegions((state) => state.map((region) => {
      region.selected = data.solutions.includes(region.name);

      return region;
    }));
  }, [data.solutions]);


  const handleSelect = useCallback((name, selected, fromMobile = false) => {
    if (onMobile && !fromMobile) {
      return;
    }

    if (selected) {
      const data = regions
        .filter(r => r.selected)
        .map(r => r.name);

      data.push(name);

      updateStepData({
        solutions: data
      });
    } else {
      const data = regions
        .filter(r => r.selected && r.name !== name)
        .map(r => r.name);

      updateStepData({
        solutions: data
      });
    }


  }, [regions, updateStepData, onMobile]);

  return (
    <OutsideClickHandler
      onOutsideClick={() => {
        onClose();
      }}
    >
      <motion.div
        initial={{
          opacity: 0,
          scale: 0
        }}
        animate={{
          opacity: 1,
          scale: 1
        }}
        exit={{
          opacity: 0,
          scale: 0
        }}
        transition={{
          duration: 0.5
        }}
        className="add-solutions-popup-container"
      >

        <div className="add-solutions-popup">
          <div className="add-solutions-popup-close">
            <img
              onClick={onClose}
              src={`${process.env.PUBLIC_URL}/assets/images/close-icon.png`}
              alt=""
            />
          </div>

          <div className="add-solutions-popup-title">Add or remove payment solutions</div>

          <div className="regions-page-list scroll-div">
            {regions.map((region, index) => (
              <div
                onClick={() => handleSelect(region.name, !region.selected, false)}
                key={index}

              >
                <SelectBox multi={true} isSelected={region.selected} icon={region.icon} title={region.name}>
                  <div className='card-btn-group-mobile'>
                    {region.selected
                      ? <div
                        onClick={() => handleSelect(region.name, !region.selected, true)}
                        className='default-button'
                      >
                        remove
                      </div>
                      : <div
                        onClick={() => handleSelect(region.name, !region.selected, true)}
                        className='default-button'
                      >
                        select
                      </div>
                    }
                  </div>
                </SelectBox>
              </div>
            ))}
          </div>

          <div className="add-solutions-popup-button">
            <Button
              title="Save"
              onClick={onClose}
            />
          </div>
        </div>


        <div className="add-solutions-popup-mobile">
          <div className="add-solutions-popup-close">
            <img
              onClick={onClose}
              src={`${process.env.PUBLIC_URL}/assets/images/X2x.png`}
              alt=""
            />
          </div>

          <div className="add-solutions-popup-title">You can add or remove solutions.
          </div>

          <div className="regions-page-list scroll-popup-mobile">
            {regions.map((region, index) => (
              <div
                onClick={() => handleSelect(region.name, !region.selected, false)}
                key={index}

              >
                <SelectBox multi={true} isSelected={region.selected} icon={region.icon} title={region.name}>
                  <div className='card-btn-group-mobile'>
                    {region.selected
                      ? <div
                        onClick={() => handleSelect(region.name, !region.selected, true)}
                        className='default-button'
                      >
                        remove
                      </div>
                      : <div
                        onClick={() => handleSelect(region.name, !region.selected, true)}
                        className='default-button'
                      >
                        select
                      </div>
                    }
                  </div>
                </SelectBox>
              </div>
            ))}
          </div>

          <div className="add-solutions-popup-button">
            <Button
              title="Save"
              onClick={onClose}
            />
          </div>

        </div>
      </motion.div>
    </OutsideClickHandler>
  );
};

export default AddSolutionsPopup;
